import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Trash2, Check, Store } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { brl, pct } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { useUnit } from "@/context/UnitContext";
import { Badge, Button, Card, Field, Input, Loading, Page, PageHeader } from "@/components/kit";

function UnitCard({ u, isCurrent, isAdmin, onSwitch, onDone }) {
  const [edit, setEdit] = useState(false);
  const [name, setName] = useState(u.name);
  const ren = useMutation({ mutationFn: () => api(`/units/${u.id}`, { method: "PUT", body: { name } }), onSuccess: () => { setEdit(false); onDone("Unidade renomeada"); }, onError: (e) => toast.error(e.message) });
  const del = useMutation({ mutationFn: () => api(`/units/${u.id}`, { method: "DELETE" }), onSuccess: () => onDone("Unidade excluída"), onError: (e) => toast.error(e.message) });
  return (
    <div className="card" style={{ borderColor: isCurrent ? "var(--brand)" : undefined }} data-testid={`unit-card-${u.id}`}>
      <div className="flex items-center gap-2">
        <Store size={17} style={{ color: "var(--brand)" }} />
        {edit ? (
          <>
            <Input autoFocus className="input-sm flex-1" value={name} onChange={(e) => setName(e.target.value)} data-testid={`unit-rename-input-${u.id}`} />
            <button className="icon-btn" onClick={() => ren.mutate()} data-testid={`unit-rename-save-${u.id}`}><Check size={15} /></button>
          </>
        ) : (
          <>
            <span className="h3 flex-1 truncate">{u.name}</span>
            {isCurrent && <Badge>Atual</Badge>}
            {isAdmin && <button className="icon-btn" onClick={() => setEdit(true)} data-testid={`unit-edit-${u.id}`}><Pencil size={14} /></button>}
            {isAdmin && !isCurrent && <button className="icon-btn" onClick={() => window.confirm(`Excluir a unidade "${u.name}"? Produtos, categorias, setores e metas dela serão apagados.`) && del.mutate()} data-testid={`unit-delete-${u.id}`}><Trash2 size={14} /></button>}
          </>
        )}
      </div>
      <div className="grid grid-cols-3 gap-3 mt-4">
        <div><div className="caption">Última conferência</div><div className="label tnum">{pct(u.last_pct)}</div></div>
        <div><div className="caption">Faturamento últimas 4</div><div className="label tnum">{brl(u.revenue_4w)}</div></div>
        <div><div className="caption">Meta</div><div className="label tnum">{pct(u.target, 0)}</div></div>
      </div>
      {!isCurrent && <Button variant="secondary" size="sm" className="mt-4" onClick={onSwitch} data-testid={`unit-switch-${u.id}`}>Trabalhar nesta unidade</Button>}
    </div>
  );
}

export default function Units() {
  const qc = useQueryClient();
  const { isAdmin } = useAuth();
  const { unit, selectUnit } = useUnit();
  const [name, setName] = useState("");
  const { data = [], isLoading } = useQuery({ queryKey: ["units-overview"], queryFn: () => api("/reports/units-overview") });
  const done = (msg) => { qc.invalidateQueries({ queryKey: ["units"] }); qc.invalidateQueries({ queryKey: ["units-overview"] }); toast.success(msg); };
  const add = useMutation({ mutationFn: () => api("/units", { method: "POST", body: { name } }), onSuccess: () => { setName(""); done("Unidade criada"); }, onError: (e) => toast.error(e.message) });
  const withData = data.filter((u) => u.avg_pct !== null);
  const max = Math.max(40, ...withData.map((u) => u.avg_pct), ...data.map((u) => u.target));
  const best = withData.length ? withData.reduce((a, b) => (a.avg_pct < b.avg_pct ? a : b)) : null;
  return (
    <Page>
      <PageHeader title="Unidades" back />
      {isLoading ? <Loading /> : (
        <div className="flex flex-col gap-5">
          <Card data-testid="units-comparison-chart">
            <div className="h2">Painel comparativo</div>
            <div className="caption">CMV médio das últimas 4 conferências de cada unidade</div>
            <div className="flex flex-col gap-4 mt-5">
              {data.map((u) => (
                <div key={u.id}>
                  <div className="flex justify-between text-sm mb-1.5"><span className="font-semibold">{u.name}</span><span className="tnum" style={{ color: u.avg_pct > u.target ? "var(--accent)" : "var(--success)" }}>{u.avg_pct === null ? "—" : pct(u.avg_pct)}</span></div>
                  <div className="bar-track" style={{ height: 12 }}>
                    <div className="bar-fill" style={{ width: `${((u.avg_pct || 0) / max) * 100}%`, background: u.avg_pct > u.target ? "var(--accent)" : "var(--brand)" }} />
                    <div className="absolute top-0 bottom-0 w-0.5" style={{ left: `${(u.target / max) * 100}%`, background: "var(--text)" }} />
                  </div>
                </div>
              ))}
            </div>
            {best && <p className="caption mt-4">Melhor desempenho: <b>{best.name}</b> ({pct(best.avg_pct)}) · traço = meta</p>}
          </Card>
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
            {data.map((u) => <UnitCard key={u.id} u={u} isCurrent={u.id === unit?.id} isAdmin={isAdmin} onSwitch={() => { selectUnit(u.id); toast.success(`Unidade: ${u.name}`); }} onDone={done} />)}
          </div>
          {isAdmin && (
            <Card>
              <div className="flex gap-3 items-end">
                <div className="flex-1"><Field label="Nova unidade"><Input placeholder="Ex.: Unidade Shopping" value={name} onChange={(e) => setName(e.target.value)} data-testid="new-unit-input" /></Field></div>
                <Button disabled={!name.trim()} loading={add.isPending} onClick={() => add.mutate()} data-testid="add-unit-button">Criar</Button>
              </div>
              <p className="caption mt-3">Cada unidade tem seus próprios produtos, categorias, conferências, metas e e-mails. Libere o acesso de gerentes e operadores em Usuários.</p>
            </Card>
          )}
        </div>
      )}
    </Page>
  );
}
